import { useState, type FormEvent } from 'react';
import { useAppStore } from '../../stores/appStore';
import { calendarApi } from '../../lib/api';

function masterId(id: string): string {
  return id.includes('__') ? id.split('__')[0] : id;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local date YYYY-MM-DD (not UTC — avoids day shift in US timezones) */
function toLocalDateInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toLocalDateTimeInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function allDayToIso(dateStr: string, isEnd: boolean): string {
  const [y, m, day] = dateStr.split('-').map(Number);
  const d = new Date(
    y,
    m - 1,
    day,
    isEnd ? 23 : 0,
    isEnd ? 59 : 0,
    isEnd ? 59 : 0,
    isEnd ? 999 : 0
  );
  return d.toISOString();
}

/**
 * Create / edit / delete calendar events.
 * Mobile-optimized form for fast entry from phones.
 */
export function EventEditor() {
  const { open, event, defaultDate } = useAppStore((s) => s.eventEditor);
  const close = useAppStore((s) => s.closeEventEditor);
  const calendars = useAppStore((s) => s.calendars);
  const refreshEvents = useAppStore((s) => s.refreshEvents);
  const user = useAppStore((s) => s.user);

  const defaultCal =
    event?.calendar_id ||
    calendars.find((c) => c.is_shared)?.id ||
    calendars[0]?.id ||
    '';

  const today = defaultDate || toLocalDateInput(new Date().toISOString());

  const [title, setTitle] = useState(event?.title || '');
  const [calendarId, setCalendarId] = useState(defaultCal);
  const [allDay, setAllDay] = useState(Boolean(event?.all_day));
  const [start, setStart] = useState(
    event
      ? event.all_day
        ? toLocalDateInput(event.start_at)
        : toLocalDateTimeInput(event.start_at)
      : `${today}T09:00`
  );
  const [end, setEnd] = useState(
    event
      ? event.all_day
        ? toLocalDateInput(event.end_at)
        : toLocalDateTimeInput(event.end_at)
      : `${today}T10:00`
  );
  const [description, setDescription] = useState(event?.description || '');
  const [location, setLocation] = useState(event?.location || '');
  const [rrule, setRrule] = useState(event?.rrule || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  function toIso(value: string, isAllDay: boolean, isEnd = false): string {
    if (isAllDay) return allDayToIso(value.slice(0, 10), isEnd);
    return new Date(value).toISOString();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) {
      setError('Please unlock to save events');
      return;
    }
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!calendarId) {
      setError('Pick a calendar');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        calendar_id: calendarId,
        title: title.trim(),
        description,
        location,
        start_at: toIso(start, allDay, false),
        end_at: toIso(end, allDay, true),
        all_day: allDay,
        rrule: rrule || null,
      };
      if (event?.id) {
        await calendarApi.updateEvent(masterId(event.id), body);
      } else {
        await calendarApi.createEvent(body);
      }
      await refreshEvents();
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!event?.id || !user) return;
    if (!confirm('Delete this event?')) return;
    setSaving(true);
    setError('');
    try {
      await calendarApi.deleteEvent(masterId(event.id));
      await refreshEvents();
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="modal-backdrop fixed inset-0 z-[60] flex items-end justify-center bg-black/60 sm:items-center sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <form
        onSubmit={onSubmit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-slate-700 bg-slate-900 p-5 shadow-2xl sm:rounded-3xl light:border-slate-200 light:bg-white"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">
            {event ? 'Edit event' : 'New event'}
          </h2>
          <button
            type="button"
            onClick={close}
            className="rounded-lg px-2 py-1 text-slate-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm text-slate-400">Title</span>
            <input
              autoFocus
              className="w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-3 text-lg font-semibold outline-none focus:border-violet-500 light:border-slate-300 light:bg-white"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's happening?"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-slate-400">Calendar</span>
            <select
              className="w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 outline-none focus:border-violet-500 light:border-slate-300 light:bg-white"
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
            >
              {calendars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => {
                const v = e.target.checked;
                setAllDay(v);
                if (v) {
                  setStart(start.slice(0, 10) || today);
                  setEnd(end.slice(0, 10) || today);
                } else {
                  setStart(`${(start || today).slice(0, 10)}T09:00`);
                  setEnd(`${(end || today).slice(0, 10)}T10:00`);
                }
              }}
              className="h-4 w-4 rounded"
            />
            All-day event
          </label>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm text-slate-400">Start</span>
              <input
                type={allDay ? 'date' : 'datetime-local'}
                className="w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 outline-none focus:border-violet-500 light:border-slate-300 light:bg-white"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm text-slate-400">End</span>
              <input
                type={allDay ? 'date' : 'datetime-local'}
                className="w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 outline-none focus:border-violet-500 light:border-slate-300 light:bg-white"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                required
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-sm text-slate-400">Location</span>
            <input
              className="w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 outline-none focus:border-violet-500 light:border-slate-300 light:bg-white"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Optional"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-slate-400">
              Repeat (optional)
            </span>
            <select
              className="w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 outline-none focus:border-violet-500 light:border-slate-300 light:bg-white"
              value={rrule}
              onChange={(e) => setRrule(e.target.value)}
            >
              <option value="">Does not repeat</option>
              <option value="FREQ=DAILY">Daily</option>
              <option value="FREQ=WEEKLY">Weekly</option>
              <option value="FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR">Weekdays</option>
              <option value="FREQ=MONTHLY">Monthly</option>
              <option value="FREQ=YEARLY">Yearly</option>
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-slate-400">Notes</span>
            <textarea
              className="w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-2.5 outline-none focus:border-violet-500 light:border-slate-300 light:bg-white"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
        </div>

        {error && (
          <p className="mt-3 rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
            {error}
          </p>
        )}

        <div className="mt-5 flex gap-2">
          {event && (
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              className="rounded-xl bg-red-500/20 px-4 py-3 font-semibold text-red-300 hover:bg-red-500/30"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={close}
            className="rounded-xl bg-slate-800 px-4 py-3 font-semibold light:bg-slate-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 rounded-xl bg-violet-600 py-3 font-bold text-white hover:bg-violet-500 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
